import React, { useState, useRef, useEffect } from 'react';
import { Plus, X, Filter as FilterIcon, ChevronDown, User, Building2, ListChecks, Globe, AlertTriangle } from 'lucide-react';

/**
 * Linear-style chip filter bar.
 *
 * Replaces the previous "Filter button + dropdown + Users button + showing-pills" stack
 * with a single row of chips. Each active filter renders as one chip with its dimension
 * label, current value, and an × to remove. Inactive dimensions sit behind a single
 * "+ Add filter" pill that lets the user choose which dimension to add next.
 *
 * Props:
 *  - statusFilters: [{id, label}] - status/date filter options
 *  - activeFilter: string - currently selected status filter id
 *  - onStatusChange: (id) => void
 *
 *  - customers: [{id, name}] - customer list (scoped by RBAC)
 *  - selectedCustomerId: string | null
 *  - onCustomerChange: (id|null) => void
 *
 *  - users: [{id, name, email, role}] - assignable users (AM + Operators)
 *  - selectedUsers: string[]
 *  - onUserToggle: (userId) => void
 *  - onUsersClear: () => void
 *
 *  - showUserFilter: boolean - hide the User chip for restricted users
 *  - totalLoads: number - load count for the "(N loads)" indicator
 */
export const FilterChipBar = ({
  statusFilters,
  activeFilter,
  onStatusChange,
  customers = [],
  selectedCustomerId,
  onCustomerChange,
  users = [],
  selectedUsers = [],
  onUserToggle,
  onUsersClear,
  showUserFilter = true,
  showStatusFilter = true,
  totalLoads,
  cbOnly = false,
  onCbOnlyChange,
  stuckOnly = false,
  onStuckOnlyChange,
  stuckCount = 0,
}) => {
  const [openMenu, setOpenMenu] = useState(null); // 'status' | 'customer' | 'user' | 'add' | null
  const [customerSearch, setCustomerSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const barRef = useRef(null);

  // Close any open menu when clicking outside the bar
  useEffect(() => {
    const handler = (e) => {
      if (!barRef.current) return;
      if (!barRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const activeStatus = statusFilters.find((f) => f.id === activeFilter);
  const isStatusActive = activeFilter && activeFilter !== 'all';
  const isCustomerActive = !!selectedCustomerId;
  const isUserActive = selectedUsers.length > 0;

  const selectedCustomer = customers.find((c) => c.id === selectedCustomerId);
  const selectedUserNames = users.filter((u) => selectedUsers.includes(u.id)).map((u) => u.name || u.email);

  const sortedCustomers = [...customers]
    .filter((c) => (c.name || '').toLowerCase().includes(customerSearch.toLowerCase()))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const filteredUsers = [...users]
    .filter((u) => `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(userSearch.toLowerCase()))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  // Which dimensions are not yet active (used for the "+ Add filter" menu)
  const addableDimensions = [
    showStatusFilter && !isStatusActive && { id: 'status', label: 'Status / Date', icon: ListChecks },
    !isCustomerActive && customers.length > 0 && { id: 'customer', label: 'Customer', icon: Building2 },
    showUserFilter && !isUserActive && users.length > 0 && { id: 'user', label: 'Assigned User', icon: User },
  ].filter(Boolean);

  const hasAnyFilter = isStatusActive || isCustomerActive || isUserActive || cbOnly || stuckOnly;

  // ---- Render helpers ----
  const Chip = ({ active, onClick, onRemove, icon: Icon, label, value, testid, menuOpen }) => (
    <div
      className={`group inline-flex items-stretch border rounded-full overflow-hidden transition-shadow ${
        active
          ? 'bg-blue-50 border-blue-300 text-blue-700 shadow-sm'
          : 'bg-[var(--tms-card)] border-[var(--tms-border)] text-[var(--tms-text-secondary)] hover:border-blue-300'
      } ${menuOpen ? 'ring-2 ring-blue-200' : ''}`}
      data-testid={testid}
    >
      <button
        type="button"
        onClick={onClick}
        className="flex items-center gap-1.5 pl-3 pr-2 py-1.5 text-sm font-medium"
      >
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className="text-xs text-[var(--tms-text-muted)]">{label}:</span>
        <span className="max-w-[160px] truncate">{value}</span>
        <ChevronDown className="w-3 h-3 ml-0.5 opacity-70" />
      </button>
      {active && onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="px-2 border-l border-blue-200 hover:bg-blue-100 transition-colors"
          aria-label="Remove filter"
          data-testid={`${testid}-remove`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );

  const Popover = ({ children, width = 'w-64' }) => (
    <div className={`absolute left-0 top-[110%] z-50 ${width} bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg shadow-lg p-2 max-h-[60vh] overflow-y-auto`}>
      {children}
    </div>
  );

  const MenuButton = ({ active, onClick, children, testid }) => (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${
        active ? 'bg-blue-100 text-blue-700' : 'hover:bg-[var(--tms-hover)] text-[var(--tms-text-secondary)]'
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );

  return (
    <div ref={barRef} className="flex items-center gap-2 flex-wrap mb-4" data-testid="filter-chip-bar">
      {/* Quick-toggles: Cross-Border only, Stuck loads */}
      {onCbOnlyChange && (
        <button
          type="button"
          onClick={() => onCbOnlyChange(!cbOnly)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border rounded-full transition-all ${
            cbOnly
              ? 'bg-orange-100 border-orange-300 text-orange-800 shadow-sm'
              : 'bg-[var(--tms-card)] border-[var(--tms-border)] text-[var(--tms-text-secondary)] hover:border-orange-300 hover:text-orange-700'
          }`}
          data-testid="chip-cb-toggle"
          title="Show only cross-border loads"
        >
          <Globe className="w-3.5 h-3.5" />
          Cross-Border
          {cbOnly && <X className="w-3 h-3 ml-0.5" />}
        </button>
      )}

      {onStuckOnlyChange && (
        <button
          type="button"
          onClick={() => onStuckOnlyChange(!stuckOnly)}
          disabled={!stuckOnly && stuckCount === 0}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border rounded-full transition-all ${
            stuckOnly
              ? 'bg-red-100 border-red-300 text-red-800 shadow-sm animate-pulse'
              : stuckCount > 0
              ? 'bg-[var(--tms-card)] border-red-200 text-red-600 hover:bg-red-50'
              : 'bg-[var(--tms-card)] border-[var(--tms-border)] text-[var(--tms-text-muted)] opacity-60 cursor-not-allowed'
          }`}
          data-testid="chip-stuck-toggle"
          title={stuckCount > 0 ? `${stuckCount} stuck load(s) (> 24h at current status)` : 'No stuck loads'}
        >
          <AlertTriangle className="w-3.5 h-3.5" />
          Stuck
          {stuckCount > 0 && (
            <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${stuckOnly ? 'bg-white text-red-700' : 'bg-red-500 text-white'}`}>
              {stuckCount}
            </span>
          )}
          {stuckOnly && <X className="w-3 h-3 ml-0.5" />}
        </button>
      )}

      {/* Divider when toggles + chips coexist */}
      {(onCbOnlyChange || onStuckOnlyChange) && (
        <div className="h-5 w-px bg-[var(--tms-border)] mx-1" aria-hidden="true" />
      )}
      {/* Status chip - always shown */}
      {showStatusFilter && (isStatusActive || openMenu === 'status') && (
        <div className="relative">
          <Chip
            active={isStatusActive}
            onClick={() => setOpenMenu(openMenu === 'status' ? null : 'status')}
            onRemove={() => { onStatusChange('all'); setOpenMenu(null); }}
            icon={ListChecks}
            label="Status"
            value={activeStatus?.label || 'All'}
            testid="chip-status"
            menuOpen={openMenu === 'status'}
          />
          {openMenu === 'status' && (
            <Popover>
              {statusFilters.map((opt) => (
                <MenuButton
                  key={opt.id}
                  active={activeFilter === opt.id}
                  onClick={() => { onStatusChange(opt.id); setOpenMenu(null); }}
                  testid={`chip-status-option-${opt.id}`}
                >
                  {opt.label}
                </MenuButton>
              ))}
            </Popover>
          )}
        </div>
      )}

      {/* Customer chip */}
      {(isCustomerActive || openMenu === 'customer') && customers.length > 0 && (
        <div className="relative">
          <Chip
            active={isCustomerActive}
            onClick={() => setOpenMenu(openMenu === 'customer' ? null : 'customer')}
            onRemove={() => { onCustomerChange(null); setOpenMenu(null); }}
            icon={Building2}
            label="Customer"
            value={selectedCustomer?.name || 'Pick one...'}
            testid="chip-customer"
            menuOpen={openMenu === 'customer'}
          />
          {openMenu === 'customer' && (
            <Popover width="w-72">
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                placeholder="Search customers..."
                autoFocus
                className="w-full mb-2 px-2 py-1.5 text-sm border rounded bg-[var(--tms-card)] text-[var(--tms-text)]"
                data-testid="chip-customer-search"
              />
              {sortedCustomers.length === 0 ? (
                <p className="text-xs text-[var(--tms-text-muted)] px-3 py-2">No customers match</p>
              ) : (
                sortedCustomers.map((c) => (
                  <MenuButton
                    key={c.id}
                    active={selectedCustomerId === c.id}
                    onClick={() => { onCustomerChange(c.id); setOpenMenu(null); setCustomerSearch(''); }}
                    testid={`chip-customer-option-${c.id}`}
                  >
                    {c.name}
                  </MenuButton>
                ))
              )}
            </Popover>
          )}
        </div>
      )}

      {/* User chip */}
      {showUserFilter && (isUserActive || openMenu === 'user') && users.length > 0 && (
        <div className="relative">
          <Chip
            active={isUserActive}
            onClick={() => setOpenMenu(openMenu === 'user' ? null : 'user')}
            onRemove={() => { onUsersClear(); setOpenMenu(null); }}
            icon={User}
            label="Assigned"
            value={
              selectedUserNames.length === 0
                ? 'Anyone'
                : selectedUserNames.length === 1
                ? selectedUserNames[0]
                : `${selectedUserNames.length} users`
            }
            testid="chip-user"
            menuOpen={openMenu === 'user'}
          />
          {openMenu === 'user' && (
            <Popover width="w-72">
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search users..."
                autoFocus
                className="w-full mb-2 px-2 py-1.5 text-sm border rounded bg-[var(--tms-card)] text-[var(--tms-text)]"
                data-testid="chip-user-search"
              />
              {filteredUsers.map((u) => {
                const checked = selectedUsers.includes(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => onUserToggle(u.id)}
                    className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${
                      checked ? 'bg-blue-100 text-blue-700' : 'hover:bg-[var(--tms-hover)] text-[var(--tms-text-secondary)]'
                    }`}
                    data-testid={`chip-user-option-${u.id}`}
                  >
                    <input type="checkbox" checked={checked} readOnly className="pointer-events-none" />
                    <span className="flex-1 truncate">{u.name || u.email}</span>
                    <span className="text-xs text-[var(--tms-text-muted)]">{u.role}</span>
                  </button>
                );
              })}
              {selectedUsers.length > 0 && (
                <button
                  type="button"
                  onClick={() => { onUsersClear(); setOpenMenu(null); }}
                  className="w-full text-left px-3 py-2 mt-1 rounded text-xs text-[var(--tms-primary)] hover:bg-[var(--tms-hover)]"
                  data-testid="chip-user-clear"
                >
                  Clear all
                </button>
              )}
            </Popover>
          )}
        </div>
      )}

      {/* + Add filter chip */}
      {addableDimensions.length > 0 && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpenMenu(openMenu === 'add' ? null : 'add')}
            className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium border border-dashed rounded-full transition-colors ${
              openMenu === 'add'
                ? 'border-blue-300 text-blue-700 bg-blue-50'
                : 'border-[var(--tms-border)] text-[var(--tms-text-muted)] hover:border-blue-300 hover:text-blue-700'
            }`}
            data-testid="chip-add-filter"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>{hasAnyFilter ? 'Add filter' : 'Filter'}</span>
          </button>
          {openMenu === 'add' && (
            <Popover>
              {addableDimensions.map((dim) => {
                const Icon = dim.icon;
                return (
                  <MenuButton
                    key={dim.id}
                    onClick={() => setOpenMenu(dim.id)}
                    testid={`chip-add-${dim.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    {dim.label}
                  </MenuButton>
                );
              })}
            </Popover>
          )}
        </div>
      )}

      {/* Clear all + count */}
      {hasAnyFilter && (
        <button
          type="button"
          onClick={() => {
            onStatusChange('all');
            onCustomerChange(null);
            onUsersClear();
            if (onCbOnlyChange) onCbOnlyChange(false);
            if (onStuckOnlyChange) onStuckOnlyChange(false);
            setOpenMenu(null);
          }}
          className="ml-1 text-xs text-[var(--tms-text-muted)] hover:text-[var(--tms-text-secondary)] underline"
          data-testid="chip-clear-all"
        >
          Clear all
        </button>
      )}

      {typeof totalLoads === 'number' && (
        <span className="ml-auto text-xs text-[var(--tms-text-muted)] whitespace-nowrap" data-testid="filter-count">
          {totalLoads} load{totalLoads === 1 ? '' : 's'}
        </span>
      )}
    </div>
  );
};

export default FilterChipBar;
